export const normalizeCompanyName = name =>
  (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

export const parseImsatCompanies = infoOptionList => {
  if (!infoOptionList) return []

  const { API_URL, CODE, NAME, IMSAT_COMPANIES } = infoOptionList
  const apiUrl = API_URL

  let companies = []

  if (IMSAT_COMPANIES) {
    try {
      const parsed = JSON.parse(IMSAT_COMPANIES)
      if (Array.isArray(parsed)) {
        companies = parsed
      }
    } catch (error) {
      console.warn('Invalid IMSAT_COMPANIES JSON in info_option', error)
    }
  }

  if (companies.length === 0 && CODE && NAME) {
    companies = [{ code: CODE, name: NAME }]
  }

  return companies
    .filter(company => company?.code && company?.name)
    .map(company => ({
      code: company.code,
      name: company.name,
      cpctmCode: company.cpctmCode || null,
      apiUrl: company.apiUrl || apiUrl,
      mergeLocal: !!company.mergeLocal,
    }))
}

const findMainCpctmCompany = companyList =>
  companyList.find(company => {
    const normalizedName = normalizeCompanyName(company.name)
    return (
      normalizedName.includes('phat trien cong nghe va thiet bi mo') ||
      normalizedName.includes('thiet bi mo')
    )
  })

export const findMainCpctmCompanyCode = companyList =>
  findMainCpctmCompany(companyList)?.code

const resolveMainCompanyImsatCode = (
  infoOptionList,
  companyList,
  cpctmCode
) => {
  const { CPCTM_IMSAT_CODE } = infoOptionList || {}
  if (CPCTM_IMSAT_CODE) {
    return CPCTM_IMSAT_CODE
  }

  const mainCompany = companyList.find(company => company.code === cpctmCode)
  const imsatCompanies = parseImsatCompanies(infoOptionList)

  const mergeEntry = imsatCompanies.find(
    company =>
      company.mergeLocal &&
      (company.cpctmCode === cpctmCode ||
        (mainCompany &&
          normalizeCompanyName(company.name) ===
            normalizeCompanyName(mainCompany.name)))
  )
  if (mergeEntry?.code) {
    return mergeEntry.code
  }

  const nameMatch = mainCompany
    ? imsatCompanies.find(
        company =>
          normalizeCompanyName(company.name) ===
          normalizeCompanyName(mainCompany.name)
      )
    : null
  if (nameMatch?.code) {
    return nameMatch.code
  }

  return cpctmCode
}

const IMSAT_SUBSIDIARY_KEYWORDS = [
  'vien khoa hoc cong nghe mo',
  'trung tam an toan mo',
]

const isImsatSubsidiaryCompany = company => {
  if (!company?.name) {
    return false
  }
  const normalizedName = normalizeCompanyName(company.name)
  return IMSAT_SUBSIDIARY_KEYWORDS.some(keyword =>
    normalizedName.includes(keyword)
  )
}

export const getMergedLocalImsatConfig = (
  infoOptionList,
  companyList,
  selectedKey,
  currentUserCompanyCode
) => {
  if (!selectedKey || !infoOptionList?.API_URL) {
    return null
  }

  const { API_URL, CPCTM_CODE } = infoOptionList
  const selectedCompany = companyList.find(company => company.code === selectedKey)

  if (isImsatSubsidiaryCompany(selectedCompany)) {
    return null
  }

  const mainCpctmCode =
    CPCTM_CODE ||
    findMainCpctmCompanyCode(companyList) ||
    currentUserCompanyCode

  const shouldMerge =
    selectedKey === mainCpctmCode ||
    (selectedKey === currentUserCompanyCode &&
      !isImsatSubsidiaryCompany(selectedCompany))

  if (!shouldMerge) {
    return null
  }

  return {
    imsatCode: resolveMainCompanyImsatCode(
      infoOptionList,
      companyList,
      selectedKey
    ),
    apiUrl: API_URL,
    cpctmCode: selectedKey,
  }
}

export const mergeWorkSchedules = (localSchedules = [], imsatSchedules = []) => {
  const merged = new Map()

  localSchedules.forEach(item => {
    merged.set(item.schedule_code, { ...item, _isImsatSource: false })
  })

  imsatSchedules.forEach(item => {
    if (!merged.has(item.schedule_code)) {
      merged.set(item.schedule_code, { ...item, _isImsatSource: true })
    }
  })

  return Array.from(merged.values()).sort(
    (first, second) => new Date(first.start_at) - new Date(second.start_at)
  )
}

export const shouldUseParentWorkScheduleView = (
  item,
  isParentCompanySelected
) => item._isImsatSource ?? isParentCompanySelected

export const getWorkScheduleViewPath = (item, isParentCompanySelected) => {
  const useParentView = shouldUseParentWorkScheduleView(
    item,
    isParentCompanySelected
  )

  return useParentView
    ? `/company-work-schedule/parent/view/${item.schedule_code}`
    : `/company-work-schedule/view/${item.schedule_code}`
}

export const enrichImsatCompaniesWithCpctmCodes = (
  imsatCompanies,
  companyList
) =>
  imsatCompanies.map(imsatCompany => {
    if (imsatCompany.cpctmCode) {
      return imsatCompany
    }

    const matchedCompany = companyList.find(
      company =>
        normalizeCompanyName(company.name) ===
        normalizeCompanyName(imsatCompany.name)
    )

    return matchedCompany
      ? { ...imsatCompany, cpctmCode: matchedCompany.code }
      : imsatCompany
  })

export const findSelectedImsatCompany = (imsatCompanies, selectedKey) =>
  imsatCompanies.find(
    company =>
      company.code === selectedKey || company.cpctmCode === selectedKey
  )

export const getImsatCpctmCodesToHide = imsatCompanies =>
  new Set(
    imsatCompanies
      .filter(company => !company.mergeLocal)
      .flatMap(company => [company.cpctmCode].filter(Boolean))
  )

export const buildImsatWorkScheduleCompanies = (
  infoOptionList,
  companyList,
  permissionViewList,
  currentUserCompanyCode
) => {
  let companies = enrichImsatCompaniesWithCpctmCodes(
    parseImsatCompanies(infoOptionList).filter(company => !company.mergeLocal),
    companyList
  )

  const { API_URL, CODE, NAME } = infoOptionList || {}
  const configuredCpctmCodes = new Set(
    companies.map(company => company.cpctmCode).filter(Boolean)
  )

  permissionViewList.forEach(permission => {
    const company = companyList.find(item => item.code === permission.access_type)
    if (!company || company.code === currentUserCompanyCode) {
      return
    }
    if (configuredCpctmCodes.has(company.code)) {
      return
    }

    const isImsatSubsidiary = isImsatSubsidiaryCompany(company)
    if (!isImsatSubsidiary) {
      return
    }

    const legacyMatch =
      CODE && NAME && normalizeCompanyName(company.name) === normalizeCompanyName(NAME)

    companies.push({
      code: legacyMatch ? CODE : company.code,
      name: company.name,
      cpctmCode: company.code,
      apiUrl: API_URL,
    })
    configuredCpctmCodes.add(company.code)
  })

  const companiesByName = new Map()
  companies.forEach(company => {
    const normalizedName = normalizeCompanyName(company.name)
    const existingCompany = companiesByName.get(normalizedName)
    if (
      !existingCompany ||
      (!existingCompany.cpctmCode && company.cpctmCode)
    ) {
      companiesByName.set(normalizedName, company)
    }
  })

  return Array.from(companiesByName.values())
}
